import React, { useEffect, useState, useMemo } from 'react';
import Table from '../../../component/common/Table';
import Pagination from '../../../component/common/table2/Pagination';
import Loader from '../../../component/common/Loader';
import shuffle from '../../../assets/svg/icons/shuffle.svg';
import RightArrow from '../../../assets/images/RightArrow.svg';
import { useLocation } from 'react-router-dom';
import { GENDER } from '../../../utils/constants';
import { errorToast, isCopanySuperOrSubAdmin, isSuperAndSubAdmin, successToast } from '../../../utils/helper';
import { ApiForRole } from '../challenges/ApiForRole';
import moment from 'moment';
import { Api } from '../../../api';
import ViewUserChallenge from './ViewUserChallenge';
import { CompanyApi } from '../../../api/companyApi';
import JoditEditor from 'jodit-react';
import ConfirmPopup from '../../../component/common/modals/ConfirmPopup';
import { TableCard } from '../../../component/common/designSystem';


const WinnerResult = ({ data, setData, handleChangeType, setNewChallenge }) => {
  const selectedTheme = JSON.parse(localStorage.getItem('selectedTheme'));
  const location = useLocation();
  const state = location.state;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [winnerList, setWinnerList] = useState([]);
  const [count, setCount] = useState({
    rewardStatus: 0,
    totalCompletedUsers: 0,
    totalRequestedWinners: 0,
    totalSelectedWinners: 0
  });
  const [loader, setLoader] = useState(false);
  const [clickedRowId, setClickedRowId] = useState(null);

  const [selectedWinners, setSelectedWinners] = useState([]);
  const [maxWinners, setMaxWinners] = useState(0);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showUserChallenge, setShowUserChallenge] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Email form states
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailHeader, setEmailHeader] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Jodit editor config
  const editorConfig = useMemo(
    () => ({
      readonly: false,
      placeholder: 'Enter email body...',
      width: 'auto',
      height: 300,
      buttons: [
        'font',
        'underline',
        'bold',
        'italic',
        '|',
        'ul',
        'ol',
        '|',
        'table',
        'outdent',
        'indent',
        '|',
        'brush',
        'paragraph',
        '|',
        'link',
        'image',
        'align',
        '|',
        'source',
        '|',
        'undo',
        'redo'
      ]
    }),
    []
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const newChallengeParam = queryParams.get("newchallenge");
    setNewChallenge(newChallengeParam);
  }, [location.search]);

  const toggleWinner = (userId) => {
    if (count?.rewardStatus === 9) {
      errorToast('Winner selection is closed. Winners have already been announced');
      return;
    }
    const isAlreadySelected = selectedWinners.includes(userId);
    if (!isAlreadySelected && selectedWinners.length >= maxWinners) {
      errorToast(`You can only select up to ${maxWinners} winners.`);
      return;
    }

    const updatedWinners = isAlreadySelected
      ? selectedWinners.filter((id) => id !== userId)
      : [...selectedWinners, userId];

    setSelectedWinners(updatedWinners);
    // Note: No API call here - winners are selected locally until email is sent
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      sortable: false,
      align: 'left',
      type: 'text'
    },
    {
      title: 'Gender',
      key: 'gender',
      sortable: false,
      align: 'left',
      type: 'text'
    },
    {
      title: 'Rank',
      key: 'rank',
      sortable: false,
      align: 'center',
      type: 'text'
    },
    {
      title: 'Date',
      key: 'wonAt',
      sortable: false,
      align: 'center',
      type: 'text'
    },
    {
      title: 'Activity Count',
      key: 'activityCount',
      sortable: false,
      align: 'center',
      type: 'text'
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      type: 'action',
      isView: true,
      handleView: (rowData) => {
        setSelectedUserId(rowData._id);
        setShowUserChallenge(true);
      }
    }
  ];

  const genderMap = {
    1: 'Male',
    2: 'Female',
  };


  const rowsData = winnerList.map((item) => {
    const baseData = {
      _id: item._id,
      name: item.name,
      email: item.email,
      status: item.status,
      reward: item?.reward,
      rewardStatus: item?.rewardStatus,
      wonAt: item?.wonAt,
      taskInfo: item?.taskInfo,
    };

    return {
      ...baseData,
      gender: (
        <div className="text-sm">
          {item.gender.length > 0 ? genderMap[item.gender[0]] || 'N/A' : 'N/A'}
        </div>
      ),
      wonAt: (
        <div className="text-sm">
          {item.wonAt ? moment(item.wonAt).format('MMM DD, YYYY') : 'N/A'}
        </div>
      ),
      rank:(
        item.taskInfo?.rank ? (
          <div className="text-sm">
            {item.taskInfo?.rank}
          </div>
        ) : (
          <div className="text-sm">N/A</div>
        )
      ),
      activityCount: (
        <div className="text-s text-decoration-nonem flex items-center justify-between ms-5">
          <span>{''}</span>
          <span className='ms-5'>{item.taskInfo?.completedTasksCount ?? 'N/A'}</span>
          <button
            onClick={() => {
              // console.log('Company data', item);
              setSelectedTask(item);
              setSelectedWinners([item?._id]);
              setClickedRowId(item?._id)
            }}
            className="ml-2 "
          >
            {selectedTheme ? (
              <>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_520_11514)">
                    <path
                      d="M4.8165 1.63306C7.67052 3.06006 9.24519 4.48707 10.6875 7.06105C11.014 7.64381 11.014 8.35603 10.6875 8.93878C9.24519 11.5127 7.67052 12.9397 4.8165 14.3668"
                      stroke="#F3F4F6"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_520_11514">
                      <rect
                        width="16"
                        height="16"
                        fill="white"
                        transform="translate(16) rotate(90)"
                      />
                    </clipPath>
                  </defs>
                </svg>
              </>
            ) : (
              <>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_520_11514)">
                    <path
                      d="M4.8165 1.63306C7.67052 3.06006 9.24519 4.48707 10.6875 7.06105C11.014 7.64381 11.014 8.35603 10.6875 8.93878C9.24519 11.5127 7.67052 12.9397 4.8165 14.3668"
                      stroke="#0B0B0B"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_520_11514">
                      <rect
                        width="16"
                        height="16"
                        fill="white"
                        transform="translate(16) rotate(90)"
                      />
                    </clipPath>
                  </defs>
                </svg>
              </>
            )}
          </button>
        </div>
      )
    };
  });

  const b2bColumns = [
    {
      title: 'Name',
      key: 'name',
      sortable: false,
      align: 'left',
      type: 'text'
    },
    {
      title: 'Gender',
      key: 'gender',
      sortable: false,
      align: 'left',
      type: 'text'
    },
     {
      title: 'Rank',
      key: 'rank',
      sortable: false,
      align: 'center',
      type: 'text'
    },
    {
      title: 'Date',
      key: 'dateNew',
      sortable: false,
      align: 'center',
      type: 'text'
    },
    {
      title: 'Email',
      key: 'email',
      sortable: false,
      align: 'center',
      type: 'text'
    },
    {
      title: 'Select',
      key: 'status',
      align: 'center',
      selectButton: 'Select as winner',
      type: 'action'
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      type: 'action',
      isView: true,
      handleView: (rowData) => {
        setSelectedUserId(rowData._id);
        setShowUserChallenge(true);
      }
    }
  ]

  const getGenderName = (value) => {
    const genderItem = GENDER.find((g) => g.value === value);
    return genderItem ? genderItem.name : 'Not specified';
  };

  const handlePagination = async (id, page, limit) => {
    fetchList(id, page, limit);
  };
  const taskData = [
    {
      title: 'Tasks',
      key: 'title',
      sortable: false,
      align: 'left',
      type: 'text'
    },
    {
      title: 'Status',
      key: 'status',
      sortable: false,
      align: 'left',
      type: 'text'
    },
    {
      title: 'wellpoints',
      key: 'wellpoints',
      sortable: false,
      align: 'center',
      type: 'text'
    }
  ];
  const taskRowData = selectedTask?.taskInfo?.tasks.map((item) => {
    const baseData = {
      id: item._id,
      title: item.title,
      status: item.status,
      wellpoints: item.wellpoints
    };

    return {
      ...baseData,
    }
  })

  const fetchList = async (id, page, limit) => {
    setLoader(true);
    try {
      const response = await Api.getWinnersList(id, page, limit);
      const responseData = response?.data || [];

      // console.log(responseData?.metadata?.currentPage, "responseData?.metadata?.currentPage", responseData?.metadata?.totalCount)
      if (responseData?.users) {
        setWinnerList(responseData?.users);
        setCurrentPage(responseData?.metadata?.currentPage);
        setTotalCount(responseData?.metadata?.totalCount);
        setRecordsPerPage(responseData?.metadata?.limit);
        setCount(
          response?.data?.summary || {
            rewardStatus: 0,
            totalCompletedUsers: 0,
            totalRequestedWinners: 0,
            totalSelectedWinners: 0
          }
        );
        setMaxWinners(response?.data?.summary?.totalRequestedWinners || 0);
        const modifiedData = responseData?.users.map((item) => ({
          ...item,
          gender: item?.gender ? getGenderName(item.gender[0]) : 'N/A',
          dateNew: item?.date ? item.date : 'N/A'
        }));
        const preSelected = modifiedData
          ?.filter((item) => item.status === 'winner')
          ?.map((item) => item._id);

        setSelectedWinners(preSelected);
      }
    } catch (error) {
      console.error('Error fetching get wellpoints count:', error);
    } finally {
      setLoader(false);
    }
  };

  const challengeSummary = async (id) => {
    try {
      const response = await CompanyApi.getChallengeSummary(id);
      const responseData = response?.data || {};
      console.log('Challenge Summary:', responseData);
    } catch (error) {
      console.error('Error fetching challenge summary:', error);
    }
  };

  useEffect(() => {
    if (state?._id) {
      fetchList(state?._id, currentPage, recordsPerPage);
      challengeSummary(state?._id);
    }
  }, [state?._id]);


  const selectRandomWinner = async (id) => {
    // console.log(count?.rewardStatus, "count.rewardStatus ==>")
    if (count?.rewardStatus === 10) {
      errorToast('Winner selection is closed. Winners have already been announced');
      return;
    }
    setLoader(true);
    const body = {
      challengeId: id
    };
    try {
      const response = await ApiForRole().addRandomWinner(body, id);
      if (response.status === 200) {
        successToast(response?.data?.message);
        fetchList(state?._id, currentPage, recordsPerPage);
      }
    } catch (error) {
      console.error('Error fetching get wellpoints count:', error);
    } finally {
      setLoader(false);
    }
  };

  const sendEmailToWinners = async () => {
    if (selectedWinners.length === 0) {
      errorToast('Please select at least one winner');
      return;
    }
    if (!emailHeader.trim()) {
      errorToast('Please enter email header');
      return;
    }
    if (!emailBody.trim()) {
      errorToast('Please enter email body');
      return;
    }

    setLoader(true);
    const body = {
      userIds: selectedWinners,
      challengeId: state?._id,
      emailHeader: emailHeader,
      emailBody: emailBody
    };
    try {
      const response = await ApiForRole().addManualSelectWinners(body, state?._id);

      if (response.status === 200) {
        successToast(response?.data?.message || 'Email sent successfully to winners');
        // Reset form and go back to table view
        setEmailHeader('');
        setEmailBody('');
        setShowEmailForm(false);
        setShowConfirmDialog(false);
        // Refresh the list to show updated winner status
        fetchList(state?._id, currentPage, recordsPerPage);
      }
    } catch (error) {
      console.error('Error sending email to winners:', error);
      errorToast('Failed to send email to winners');
    } finally {
      setLoader(false);
    }
  };

  const handleEmailNotificationClick = () => {
    if (selectedWinners.length === 0) {
      errorToast('Please select at least one winner before sending email');
      return;
    }
    setShowEmailForm(true);
  };

  const handleSendEmailClick = () => {
    if (!emailHeader.trim()) {
      errorToast('Please enter email header');
      return;
    }
    if (!emailBody.trim()) {
      errorToast('Please enter email body');
      return;
    }
    setShowConfirmDialog(true);
  };

  const getSelectedWinnersData = () => {
    return winnerList
      .filter(user => selectedWinners.includes(user._id))
      .map(user => ({ name: user.name, email: user.email }));
  };

  return (
    <div>
      {loader ? <Loader /> : null}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <ConfirmPopup
          open={showConfirmDialog}
          setOpen={setShowConfirmDialog}
          message={
            <div>
              <p className="mb-3">Ready to notify the winners with an email?</p>
              <div className="max-h-60 overflow-y-auto">
                <table className="w-full text-left text-sm">
                  <thead className="border-b">
                    <tr>
                      <th className="py-2 pr-4 font-medium">Name</th>
                      <th className="py-2 font-medium">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSelectedWinnersData().map((winner, index) => (
                      <tr key={index} className="border-b last:border-b-0">
                        <td className="py-2 pr-4">{winner.name}</td>
                        <td className="py-2 text-gray-600">{winner.email}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          }
          setAccepted={sendEmailToWinners}
        />
      )}

      {showUserChallenge ? (
        <ViewUserChallenge
          challengeId={state?._id}
          userId={selectedUserId}
          onBack={() => {
            setShowUserChallenge(false);
            setSelectedUserId(null);
          }}
        />
      ) : showEmailForm ? (
        /* Email Form View */
        <div className="flex flex-col w-full mt-5 bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder border-[1.5px] border-shoorah-borderGray lg:rounded-2xl rounded-xl my-5 lg:p-6 p-4 pb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-medium text-xl">Send Email to Winners ({selectedWinners.length} selected)</h3>
          </div>
          <div className="max-w-[845px]">
            <div className="mb-4">
              <label className="text-base font-medium mb-2 block">Email Header</label>
              <input
                type="text"
                placeholder="Enter Email Header"
                value={emailHeader}
                onChange={(e) => setEmailHeader(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-shoorah-primary dark:bg-shoorah-darkBgColor dark:border-gray-600 dark:text-white"
              />
            </div>

            <div className="mt-4">
              <label className="text-base font-medium mb-2 block">Email Body</label>
              <div className="relative">
                <JoditEditor
                  value={emailBody}
                  config={editorConfig}
                  onChange={(content) => setEmailBody(content)}
                  className="!bg-shoorah-darkBg dark:!text-shoorah-lightBlack"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-between mt-6">
            <button
              type="button"
              onClick={() => setShowEmailForm(false)}
              className="inline-flex text-xs sm:text-base font-medium items-center justify-center rounded-3xl border border-gray-400 px-5 py-2 text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none w-full sm:w-auto"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleSendEmailClick}
              className="btn-primary btn-sm w-full sm:w-auto"
            >
              Send Email
              <img className="ml-2" src={RightArrow} alt="send" />
            </button>
          </div>
        </div>
      ) : (
        /* Table View */
        <>
          <div className="flex flex-wrap md:flex-nowrap gap-[15px] w-full p-4">
            {/* Left Div */}
            {isSuperAndSubAdmin() && (
              <div
                className="flex-3 min-w-[200px] md:w-[75%] flex flex-col bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder border-[1.5px] border-shoorah-borderGray lg:rounded-2xl rounded-xl"
              >
                <div className="flex-1 p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-medium text-xl">{`Select Winner (${selectedWinners?.length}/${count?.totalRequestedWinners})`}</h3>
                    <div className="ml-3">
                      <button
                        type="button"
                        onClick={() => selectRandomWinner(state?._id)}
                        className="inline-flex text-xs sm:text-base font-medium items-center justify-center px-2 gap-2 h-[40px] rounded-full border border-[#1F1F1F] bg-[#F1F1F1] text-[#1F1F1F]"
                      >
                        <img className="ml-2" src={shuffle} alt="" />
                        Random Pick
                      </button>
                    </div>
                  </div>
                  <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: recordsPerPage,
            onPageChange: (page) => handlePagination(state?._id, page, recordsPerPage)
          }}
        >
          <Table
                    columns={columns}
                    data={rowsData}
                    name={'winner_results'}
                    bottomBorder={totalCount > 10}
                    loader={loader}
                    selectedWinners={selectedWinners}
                    toggleWinner={toggleWinner}
                    activeRowId={clickedRowId}
                  />
        </TableCard>
                </div>
              </div>
            )}
            {isCopanySuperOrSubAdmin() && (
              <div
                className="flex-3 min-w-[200px] md:w-[100%] flex flex-col bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder border-[1.5px] border-shoorah-borderGray lg:rounded-2xl rounded-xl"
              >
                <div className="flex-1 p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-medium text-xl">{`Select Winner (${selectedWinners?.length}/${count?.totalRequestedWinners})`}</h3>
                    <div className="ml-3">
                      <button
                        type="button"
                        onClick={() => selectRandomWinner(state?._id)}
                        className="inline-flex text-xs sm:text-base font-medium items-center justify-center px-2 gap-2 h-[40px] rounded-full border border-[#1F1F1F] bg-[#F1F1F1] text-[#1F1F1F]"
                      >
                        <img className="ml-2" src={shuffle} alt="" />
                        Random Pick
                      </button>
                    </div>
                  </div>
                  <Table
                    columns={b2bColumns}
                    data={rowsData}
                    name={'winner_results'}
                    bottomBorder={totalCount > 10}
                    loader={loader}
                    selectedWinners={selectedWinners}
                    toggleWinner={toggleWinner}
                    activeRowId={clickedRowId}
                  />
                </div>
              </div>
            )}

            {/* Right Div - Task Progress */}
            {isSuperAndSubAdmin() && (
              <div className="flex-1 min-w-[200px] md:w-[25%] flex flex-col bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder border-[1.5px] border-shoorah-borderGray lg:rounded-2xl rounded-xl">
                <div className="flex-1 p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-medium text-xl">Task Progress</h3>
                  </div>
                  {selectedTask && (
                    <>
                      <div className="flex-1">
                        <Table
                          columns={taskData}
                          data={taskRowData}
                          name={'steps_count'}
                          bottomBorder={totalCount > 10}
                          loader={loader}
                        />
                      </div>
                      <div className="flex-1 mt-4">
                        <button
                          type="button"
                          onClick={() => toggleWinner(selectedTask?._id)}
                          className={`text-sm px-5 h-[40px] rounded-full border border-[#0B1D2E] ${selectedWinners.includes(selectedTask?._id) ? "!bg-[#95e624]" : ""
                            } text-[#0B1D2E]`}
                        >
                          {selectedWinners.includes(selectedTask?._id) ? "Selected as Winner" : "Select as Winner"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="mt-5">
            
          </div>
          <div className="flex justify-end mt-6">
            <button
              onClick={handleEmailNotificationClick}
              type="button"
              className="btn-primary btn-sm w-full sm:w-auto"
            >
              Email Notification
              <img className="ml-2" src={RightArrow} alt="edit" />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default WinnerResult;